import {
  EntityTypes,
  RegistrationStatus,
  SubscriptionStatus,
  SupplierImportSource,
  SupplierRegistrationEvent,
} from './enums';
import { Coordinates, Org } from './org';
import { SupplierSubscription } from './subscription';

export interface Supplier extends Org {
  supplierSubscription?: SupplierSubscription;
  supplierData: SupplierData;
  adminOrg?: boolean;
  statusFlags?: SupplierStatusFlags;
}

export interface SupplierData {
  description: string;
  industry: string;
  primaryNAICS?: NAICS;
  naics: NAICS[];
  mainContact: Contact;
  keyContact1: Contact;
  keyContact2: Contact;
  primaryIndustrySector: string;
  primaryBusinessActivity: string;
  legalOrganizationStatus: string;
  productsAndServices: string[];
  primaryShareholder: string;
  establishmentDate: string;
  establishmentYear?: string;
  multipleSites: boolean;
  geographiesServed?: string[];
  currentSRARange?: string;
  customerTypes?: string[];
  top5Industries?: string[];
  primarySalesChannels?: string[];
  noSupportCompanies?: string[];
  demographicCertifications: Certification[];
  demographicDesignations: string[];
  industryCertifications: Certification[];
  digitalProducts?: string[];
  outsourcedServices?: string[];
  ownershipQuestion?: string;
  boardOfAdvisors?: boolean;
  bOAExpectations?: string[];
  numberOfEmployees?: number;
  numberOfContractors?: number;
  ethnicMinorityPercentage?: number;
  womenEmployeePercentage?: number;
  executiveLeadershipRoles?: string[];
  financingUsed?: string[];
  costOfFinancing?: string;
  useForOutsideCapital?: string[];
  currentRevenueRange?: string;
  annualGrowthRate?: number;
  yearlyTotalRevenue?: {
    year: number;
    revenue: number;
  }[];
  capabilitySheet?: CapabilitySheet;
  awards?: Award[];
  previousCustomers?: string[];
  currentCustomers?: string[];
  analysisData?: string;
}

export interface SupplierStatusFlags {
  hasCompletedSupplierIntake?: boolean;
  hasTrendsAndOpportunitiesReport?: boolean;
  hasCompletedSRA?: boolean;
  hasSRAReport?: boolean;
  hasCompletedSGP?: boolean;
  hasScheduledSGPSession?: boolean;
  hasCompletedSGPSession?: boolean;
  hasSGPReport?: boolean;
  hasFeedbackRequested?: boolean;
  doNotNudge?: boolean;
}

//JSONSchema for SupplierStatusFlags
export const SupplierStatusFlagsSchema = {
  type: 'object',
  properties: {
    hasCompletedSupplierIntake: { type: 'boolean' },
    hasCompletedSRA: { type: 'boolean' },
    hasCompletedSGP: { type: 'boolean' },
    hasScheduledSGPSession: { type: 'boolean' },
    hasCompletedSGPSession: { type: 'boolean' },
    hasFeedbackRequested: { type: 'boolean' },
    doNotNudge: { type: 'boolean' },
  },
  additionalProperties: false,
};

export interface Contact {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
}

export interface Certification {
  type?: string;
  name?: string;
  date?: number;
  agency?: string;
  number?: string;
  status?: string;
  fileKey?: string;
}

export interface CapabilitySheet {
  url?: string;
  fileKey?: string;
}
export interface SupplierImportData {
  orgName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  website: string;
  description: string;
  industry: string;
  orgHash: string;
  primaryNAICS: NAICS;
  naics?: NAICS[];
  contactPhone: string;
  contactEmail: string;
  capabilitySheet: string;
  geographiesServed: string[];
  logo: string;
  mainContact: Contact;
  keyContact1: Contact;
  keyContact2: Contact;
  primaryIndustrySector: string;
  primaryBusinessActivity: string;
  legalOrganizationStatus: string;
  productsAndServices: string[];
  primaryShareholder: string;
  previousCustomers: string[];
  awards: Award[];
  establishmentDate: string;
  multipleSites: boolean;
  customerTypes: string[];
  demographicCertifications: string[];
  demographicDesignations: string[];
  industryCertifications: string[];
  executiveLeadershipRoles: string[];
  avatar?: string;
  supplierDataAnalysis?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  linkedInURL?: string;
}

export interface SupplierImportMessage {
  url: string;
  companyName: string;
  source: SupplierImportSource;
  sourceId: string;
  requestToken?: string;
  orgId? : string;
}

export interface SupplierPublishedMessage {
  supplier: SupplierImportData;
  source: SupplierImportSource;
  url: string;
  sourceId: string;
  requestToken?: string;
  orgId? : string;
  error?: string;
}

export interface NAICS {
  code: string;
  label: string;
  description?: string;
}
export interface Award {
  name: string;
  year: string;
}

export interface SupplierRegistrationAPIRequest {
  firstName: string;
  lastName: string;
  userEmail: string;
  title: string;
  orgName: string; //Company Name
  address1: string; //Street Address
  address2?: string; //Secondary Address Line
  city: string;
  state: string;
  zip: string;
  website: string;
  discountCode?: string;
  registrationCode: string;
}

//JSONSchema for SupplierRegistrationAPIRequest
export const SupplierRegistrationAPIRequestSchema = {
  type: 'object',
  properties: {
    firstName: { type: 'string', minLength: 1, maxLength: 100 },
    lastName: { type: 'string', minLength: 1, maxLength: 100 },
    userEmail: { type: 'string', format: 'email' },
    title: { type: 'string', minLength: 2, maxLength: 100 },
    orgName: { type: 'string', minLength: 3, maxLength: 100 },
    address1: { type: 'string', minLength: 1, maxLength: 100 },
    address2: { type: 'string', minLength: 0, maxLength: 100 },
    city: { type: 'string', minLength: 2, maxLength: 100 },
    state: { type: 'string', minLength: 2, maxLength: 100 },
    zip: { type: 'string', minLength: 5, maxLength: 10 },
    website: { type: 'string', format: 'uri' },
    discountCode: { type: 'string' },
    registrationCode: { type: 'string' },
  },
  required: [
    'firstName',
    'lastName',
    'userEmail',
    'title',
    'orgName',
    'address1',
    'city',
    'state',
    'zip',
    'website',
    'registrationCode',
  ],
  additionalProperties: false,
};

export interface SupplierRegistration {
  registrationId: string;
  entityType: EntityTypes;
  firstName: string;
  lastName: string;
  userEmail: string;
  title: string;
  orgName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  website: string;
  discountCode?: string;
  status: RegistrationStatus;
  registrationCode?: string;
  created: number;
  lastUpdated: number;
  orgId?: string;
  userId?: string;
  events?: SupplierRegistrationEvent[];
}

export interface SupplierRegistrationMetrics {
  recentRegistrations: SupplierRegistration[];
}


export interface SupplierRegistrationCode {
  codeId: string;
  entityType: EntityTypes;
  code: string;
  isActive: boolean;
  created: number;
  expirationDate?: number;
}

export interface CreateRegistrationCodeRequest {
  code: string;
  expirationDate?: number;
}

export const CreateRegistrationCodeRequestSchema = {
  type: 'object',
  properties: {
    code: { type: 'string', minLength: 7, maxLength: 10, pattern: '^[A-Z0-9]+$' },
    expirationDate: { type: 'number' },
  },
  required: ['code'],
  additionalProperties: false,
};

export interface UpdateRegistrationCodeRequest {
  isActive?: boolean;
  expirationDate?: number;
}

export const UpdateRegistrationCodeRequestSchema = {
  type: 'object',
  properties: {
    isActive: { type: 'boolean' },
    expirationDate: { type: 'number' },
  },
  additionalProperties: false,
};
export interface CreateSupplier {
  firstName: string;
  lastName: string;
  userEmail: string;
  title: string;
  orgName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  website: string;
}

export interface CompleteSupplierRegistrationMessage {
  registrationId: string;
  supplierId: string;
}

export interface ReportAlertMessage {
  supplierId: string;
  userEmail?: string;
  reportType: string;
  reportUrl: string;
  reportDate: string;
}

export interface SupplierMetrics {
  recentMilestones: Supplier[];
}

export interface UpdateSupplierAPIRequest {
    orgName?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    coordinates?: Coordinates;
    website?: string;
    linkedInURL?: string;
    contactPhone?: string;
    contactEmail?: string;
}

export const UpdateSupplierAPIRequestSchema = {
    type: 'object',
    properties: {
        orgName: { type: 'string', minLength: 3, maxLength: 100 },
        address1: { type: 'string', minLength: 1, maxLength: 100 },
        address2: { type: 'string', minLength: 0, maxLength: 100 },
        city: { type: 'string', minLength: 2, maxLength: 100 },
        state: { type: 'string', minLength: 2, maxLength: 100 },
        zipCode: { type: 'string', minLength: 5, maxLength: 10 },
        country: { type: 'string', minLength: 2, maxLength: 100 },
        coordinates: {
            type: 'object',
            properties: {
                latitude: { type: 'number' },
                longitude: { type: 'number' },
            },
            required: ['latitude', 'longitude'],
        },
        website: { type: 'string', format: 'uri' },
        linkedInURL: { type: 'string', format: 'uri' },
        contactPhone: { type: 'string', minLength: 7, maxLength: 20 },
        contactEmail: { type: 'string', format: 'email' },
    },
    additionalProperties: false,
}; 

export interface SupplierListItem {
  orgId: string;
  orgName: string;
  statusFlags?: SupplierStatusFlags;
}